import { useCallback, useMemo, useState } from 'react';
import { variantStockState, availableVariants, inStockWidths } from '../utils/stock';
import { awningLeadTime } from '../config/leadTimes';
import { getSaleState } from '../utils/pricing';
import { addonLines, encodeAddons, MAX_ADDON_QTY } from '../utils/addons';

// Everything a configurable awning needs to be configured, in one place.
//
// Two surfaces use this: the compact "Check Availability & Price" widget on
// the product page (AwningConfigurator) and the full /configure/:slug page
// (ConfiguratorPage). The rules - which sizes exist, what each costs, which
// (size x colour) is in stock, the lead time, where "Buy now" goes - live
// here, so the two can never disagree. The components own only their layout.
//
// Sizes come from `variant_prices` (one row per priced width x projection),
// cross-checked against `size_matrix` so a size the admin has not marked
// available is not offered. Colours are the product's `color_variants`,
// grouped into popular / solid / stripe for the pickers.
//
// Add-ons are the product's `accessory_options` (resolved by the server).
// Included ones are always on the order; paid ones carry a quantity here, and
// `total` is the awning plus the chosen add-ons - the figure every price
// display and the express wallets show. The checkout link carries the chosen
// add-ons; the server prices them again from the database.
//
// `initial` may carry `width`, `projection` and `colourCode` (e.g. from the
// URL) so a link can land on a preconfigured awning. When the caller passes
// `colour` and `onColourChange`, the colour selection is controlled by the
// caller (the product page drives its gallery from it); otherwise the hook
// keeps it itself.

function num(v) {
  const n = Number(v);
  return Number.isFinite(n) ? n : null;
}

export default function useAwningConfig(product, initial = {}) {
  const variants = useMemo(() => product?.color_variants || [], [product]);
  const sm = product?.size_matrix || {};
  const widths = useMemo(() => sm.widths || [], [sm.widths]);
  const projections = useMemo(() => sm.projections || [], [sm.projections]);
  const available = useMemo(() => sm.available || [], [sm.available]);

  // -- Sizes -----------------------------------------------------------------
  // One entry per priced size, in width then projection order. `offered`
  // honours the size matrix; a priced size the matrix has switched off is
  // kept out of the chips.
  const sizes = useMemo(() => {
    const list = (product?.variant_prices || [])
      .map((v) => ({ width: num(v.width), projection: num(v.projection), price: v.price != null ? Number(v.price) : null }))
      .filter((v) => v.width != null && v.projection != null);
    const offered = (w, p) => {
      if (!widths.length || !projections.length) return true;
      const wi = widths.indexOf(w);
      const pi = projections.indexOf(p);
      if (wi === -1 || pi === -1) return true;
      return !!(available[pi] && available[pi][wi]);
    };
    return list
      .filter((v) => offered(v.width, v.projection))
      .sort((a, b) => a.width - b.width || a.projection - b.projection);
  }, [product, widths, projections, available]);

  const [selectedWidth, setSelectedWidth] = useState(() => (initial.width != null ? String(initial.width) : ''));
  const [selectedProjection, setSelectedProjection] = useState(() => (initial.projection != null ? String(initial.projection) : ''));
  const [customMode, setCustomMode] = useState(false);
  const [customWidth, setCustomWidth] = useState('');
  const [customProjection, setCustomProjection] = useState('');

  const selectSize = useCallback((width, projection) => {
    setCustomMode(false);
    setSelectedWidth(String(width));
    setSelectedProjection(String(projection));
  }, []);

  const selectedSize = useMemo(
    () => sizes.find((s) => String(s.width) === selectedWidth && String(s.projection) === selectedProjection) || null,
    [sizes, selectedWidth, selectedProjection]
  );

  // -- Colours -----------------------------------------------------------------
  const popular = useMemo(() => variants
    .filter((v) => v.popular)
    .sort((a, b) => {
      if (a.popular_rank != null && b.popular_rank != null) return a.popular_rank - b.popular_rank;
      if (a.popular_rank != null) return -1;
      if (b.popular_rank != null) return 1;
      return 0;
    }), [variants]);
  const mostPopularCode = popular[0]?.code ?? null;
  const solids = useMemo(() => variants.filter((v) => !v.popular && !v.code?.startsWith('AW-ST')), [variants]);
  const stripes = useMemo(() => variants.filter((v) => !v.popular && v.code?.startsWith('AW-ST')), [variants]);

  const [internalColour, setInternalColour] = useState(() => {
    if (!initial.colourCode) return null;
    const i = variants.findIndex((v) => v.code === initial.colourCode);
    return i === -1 ? null : i;
  });
  const controlled = typeof initial.onColourChange === 'function';
  const selectedColour = controlled ? (initial.colour ?? null) : internalColour;
  const onColourChange = initial.onColourChange;
  const setSelectedColour = useCallback((idx) => {
    if (controlled) onColourChange(idx);
    else setInternalColour(idx);
  }, [controlled, onColourChange]);
  const toggleColour = useCallback((idx) => {
    setSelectedColour(selectedColour === idx ? null : idx);
  }, [selectedColour, setSelectedColour]);
  const colour = selectedColour != null ? variants[selectedColour] || null : null;
  const colourName = colour?.name || '';
  const colourCode = colour?.code || '';

  // -- Derived state -------------------------------------------------------------
  const sale = getSaleState(product || {});
  const availableNow = useMemo(() => (product ? availableVariants(product) : []), [product]);
  const stockWidths = useMemo(() => (product ? inStockWidths(product) : new Set()), [product]);

  const displayWidth = customMode ? customWidth.trim() : selectedWidth;
  const displayProjection = customMode ? customProjection.trim() : selectedProjection;
  const dimsReady = customMode
    ? !!(customWidth.trim() && customProjection.trim())
    : !!(selectedWidth && selectedProjection);

  const price = !customMode && selectedSize && selectedSize.price != null ? selectedSize.price : null;

  // A standard size needs BOTH a size and a colour before the exact variant
  // can be identified; a custom size is made to order by definition.
  const variantReady = customMode ? dimsReady : (dimsReady && selectedColour != null);
  const variantState = customMode
    ? 'made-to-order'
    : (variantReady && product ? variantStockState(product, selectedWidth, colourCode) : 'unknown');
  const isInStock = variantState === 'in-stock';
  const leadTime = awningLeadTime({ custom: customMode, width: displayWidth, projection: displayProjection, inStock: isInStock });

  // -- Add-ons -------------------------------------------------------------------
  const accessories = useMemo(() => product?.accessory_options || [], [product]);
  const [addonQty, setAddonQtyState] = useState({});
  const setAddonQty = useCallback((slug, qty) => {
    const q = Math.max(0, Math.min(MAX_ADDON_QTY, Math.floor(Number(qty)) || 0));
    setAddonQtyState((prev) => ({ ...prev, [slug]: q }));
  }, []);
  const addons = useMemo(() => addonLines(accessories, addonQty), [accessories, addonQty]);
  const total = price != null ? price + addons.total : null;

    // Where "Buy now" goes. Null until the variant is identified and priced;
  // the caller then offers a quote request instead.
  const checkoutUrl = useMemo(() => {
    if (!product || !variantReady || price == null) return null;
    const params = new URLSearchParams();
    params.set('product', product.slug || product._id);
    params.set('w', String(selectedWidth));
    params.set('p', String(selectedProjection));
    if (colourName) params.set('colour', colourName);
    if (colourCode) params.set('colour_code', colourCode);
    const addonParam = encodeAddons(addonQty);
    if (addonParam) params.set('addons', addonParam);
    return `/checkout?${params.toString()}`;
  }, [product, variantReady, price, selectedWidth, selectedProjection, colourName, colourCode, addonQty]);

  return {
    // data
    variants, popular, solids, stripes, mostPopularCode,
    sizes, widths, projections, available,
    availableNow, stockWidths, sale,
    // size
    selectedWidth, setSelectedWidth, selectedProjection, setSelectedProjection, selectSize, selectedSize,
    customMode, setCustomMode, customWidth, setCustomWidth, customProjection, setCustomProjection,
    displayWidth, displayProjection, dimsReady,
    // colour
    selectedColour, setSelectedColour, toggleColour, colour, colourName, colourCode,
    // add-ons
    accessories, addonQty, setAddonQty, addons, total,
    // derived
    price, variantReady, variantState, isInStock, leadTime, checkoutUrl,
  };
}
